import { InspectionResultStatus } from 'entities/InspectionResult';
import createAction from 'store/utils/createAction';
import createAPIAction from 'store/utils/createAPIAction';

const serviceName = 'repairRequest';

export const getRepairRequest = createAPIAction(
  `${serviceName}/GET_REPAIR_REQUEST`,
  ({ referenceNum }: { referenceNum: string | null }) => ({
    endpoint: `/repair-service/repair-requests?referenceNum=${referenceNum}`,
    method: 'GET',
  })
);

export const getRepairRequestDiagnosis = createAPIAction(
  `${serviceName}/GET_REPAIR_REQUEST_DIAGNOSIS`,
  ({ repairRequestId }: { repairRequestId: string | null }) => ({
    endpoint: `/repair-service/repair-requests/${repairRequestId}/diagnosis`,
    method: 'GET',
  })
);

export const getRepairRequestCar = createAPIAction(
  `${serviceName}/GET_REPAIR_REQUEST_CAR`,
  ({ repairRequestId }: { repairRequestId: string | null }) => ({
    endpoint: `/repair-service/repair-requests/${repairRequestId}/car`,
    method: 'GET',
  })
);

export const getConsumer = createAPIAction(
  `${serviceName}/GET_CONSUMER`,
  ({ consumerId }: { consumerId: number | null }) => ({
    endpoint: `/consumer-service/admin/consumer/${consumerId}`,
    method: 'GET',
  })
);

export const getFleet = createAPIAction(
  `${serviceName}/GET_FLEET`,
  ({ fleetId }: { fleetId: string | null }) => ({
    endpoint: `/consumer-service/admin/fleet/${fleetId}`,
    method: 'GET',
  })
);

export const clearRepairRequest = createAction(
  `${serviceName}/CLEAR_REPAIR_REQUEST`
);

export const updateCustomerFacingNotes = createAPIAction(
  `${serviceName}/UPDATE_CUSTOMER_FACING_NOTES`,
  ({
    customerFacingNotes,
    repairId,
  }: {
    customerFacingNotes: string;
    repairId: string;
  }) => ({
    endpoint: `/repair-service/repair-requests/${repairId}`,
    data: { customerFacingNotes },
    method: 'PATCH',
  })
);

export const updateInternalNotes = createAPIAction(
  `${serviceName}/UPDATE_CUSTOMER_FACING_NOTES`,
  ({
    internalNotes,
    repairId,
  }: {
    internalNotes: string;
    repairId: string;
  }) => ({
    endpoint: `/repair-service/repair-requests/${repairId}`,
    data: { internalNotes },
    method: 'PATCH',
  })
);

export const updateCarLastOilChange = createAPIAction(
  `${serviceName}/UPDATE_CAR_LAST_OIL_CHANGE`,
  ({
    consumerId,
    carId,
    lastOilChangeDate,
    lastOilChangeMileage,
  }: {
    consumerId: number;
    carId: number;
    lastOilChangeDate: string | null;
    lastOilChangeMileage: number | null;
  }) => ({
    endpoint: `/consumer-service/admin/consumer/${consumerId}/car/${carId}`,
    data: {
      lastOilChangeDate,
      lastOilChangeMileage,
    },
    method: 'PATCH',
  })
);

export const getConsumerCar = createAPIAction(
  `${serviceName}/GET_CONSUMER_CAR`,
  ({
    consumerId,
    consumerCarId,
  }: {
    consumerId: number;
    consumerCarId: number;
  }) => ({
    endpoint: `/consumer-service/admin/consumer/${consumerId}/cars/${consumerCarId}`,
    method: 'GET',
  })
);

export const getPartnerInfo = createAPIAction(
  `${serviceName}/GET_PARTNER_INFO`,
  (partnerId: string) => ({
    endpoint: `/consumer-service/admin/partner/${partnerId}`,
    method: 'GET',
  })
);

export const updateRepairRequestInspection = createAPIAction(
  `${serviceName}/UPDATE_MPI`,
  ({
    repairRefNum,
    mpiId,
    rejectionReason,
    otherRejectionReason,
    technician,
    status,
    technicianLastSaved,
  }: {
    repairRefNum: string;
    mpiId: string | null;
    rejectionReason: string | null;
    otherRejectionReason: string | null;
    technician?: string;
    status: 'PERFORMED' | 'NOT_PERFORMED' | 'COMPLETED';
    technicianLastSaved?: Date;
  }) => ({
    endpoint: `/repair-service/admin/mpi/repair-requests/${repairRefNum}`,
    data: {
      mpiId,
      rejectionReason,
      otherRejectionReason,
      technician,
      status,
      technicianLastSaved,
    },
    method: 'PUT',
  })
);

export const getMpiPerformedStatus = createAPIAction(
  `${serviceName}/GET_MPI_NOT_PERFORMED_STATUS`,
  ({ repairRefNum }: { repairRefNum: string }) => ({
    endpoint: `/repair-service/admin/mpi/repair-requests/${repairRefNum}`,
    method: 'GET',
  })
);

export const addSuggestedRepair = createAPIAction(
  `${serviceName}/SUBMIT_RECOMMENDED_TOOLS`,
  (
    repairRefNum: string,
    result: {
      repairId: number | null;
      severity?: InspectionResultStatus;
      symptoms?: string[];
      additionalInfo?: string;
      imageUrls?: string[];
      customServiceRequest?: string | null;
      fmcCustomRepairId?: string | null;
    },
    responsiblePartyId?: string
  ) => ({
    endpoint: `/repair-service/repair-requests/${repairRefNum}/recommended-repairs`,
    method: 'POST',
    data: {
      origin: 'FIELDOPS',
      recommendedRepairs: [
        {
          repairId: result.repairId,
          inspectionDetails: {
            severity: result.severity,
            symptoms: result.symptoms,
            additionalInfo: result.additionalInfo,
            imageUrls: result.imageUrls,
          },
          customRepairText: result.customServiceRequest,
          fmcCustomRepairId: result.fmcCustomRepairId,
          responsiblePartyAuthorizationDetails: {
            responsiblePartyId,
          },
        },
      ],
    },
  })
);

export const getSuggestedRepairs = createAPIAction(
  `${serviceName}/GET_SUGGESTED_REPAIRS`,
  ({ repairRefNum }: { repairRefNum: string }) => ({
    endpoint: `/repair-service/repair-requests/${repairRefNum}/recommended-repairs`,
    method: 'GET',
  })
);

export const updateSuggestedRepairDetails = createAPIAction(
  `${serviceName}/UPDATE_SUGGESTED_REPAIR_DETAILS`,
  ({
    referenceNum,
    suggestedRepairId,
    data,
  }: {
    referenceNum: string;
    suggestedRepairId: number;
    data: any;
  }) => ({
    endpoint: `/repair-service/repair-requests/${referenceNum}/recommended-repairs/${suggestedRepairId}`,
    method: 'PUT',
    data,
  })
);

export const deleteSuggestedRepair = createAPIAction(
  `${serviceName}/UPDATE_SUGGESTED_REPAIR_DETAILS`,
  (suggestedRepairId: number) => ({
    endpoint: `/repair-service/admin/mpi/recommended-repairs/${suggestedRepairId}`,
    method: 'DELETE',
  })
);

export const getOpenRequestsForCar = createAPIAction(
  `${serviceName}/GET_OPEN_REQUESTS_FOR_CAR`,
  ({
    consumerCarId,
    states,
    sort,
  }: {
    consumerCarId: string;
    states: string[];
    sort?: string;
  }) => ({
    endpoint: `/repair-service/repair-requests/cars/${consumerCarId}`,
    method: 'GET',
    params: {
      states,
      sort: sort ?? null,
    },
  })
);

export const updateInspectionPerformedStatus = createAPIAction(
  `${serviceName}/UPDATE_INSPECTION_PERFORMED_STATUS`,
  ({
    repairRefNum,
    data,
  }: {
    repairRefNum: string;
    data: {
      lastSent?: Date;
      lastSentByName?: string;
      lastSentByUserId?: number;
      serviceAdvisorLastSaved?: Date;
      serviceAdvisorName?: string;
      serviceAdvisorUserId?: number;
    };
  }) => ({
    endpoint: `/repair-service/admin/mpi/repair-requests/${repairRefNum}`,
    method: 'PATCH',
    data,
  })
);

export const getResponsiblePartyTypes = createAPIAction(
  `${serviceName}/GET_RESPONSIBLE_PARTY_TYPES`,
  () => ({
    endpoint: '/repair-service/admin/responsible-parties/types',
    method: 'GET',
  })
);

export const getResponsiblePartiesByType = createAPIAction(
  `${serviceName}/GET_RESPONSIBLE_PARTIES_BY_TYPE`,
  ({ responsiblePartyTypeId }: { responsiblePartyTypeId: string }) => ({
    endpoint: `/repair-service/admin/responsible-parties/by-type?responsiblePartyTypeId=${responsiblePartyTypeId}`,
    method: 'GET',
  })
);

export const setAllAvailableResponsibleParties = createAction(
  'SET_ALL_AVAILABLE_RESPONSIBLE_PARTIES'
);
